import { useEffect } from 'react'
import { Trophy, FlagTriangleRight, MapPin, Shield, Users, Plane } from 'lucide-react'
import { useMatches, useCreateMatch } from '../hooks/useMatches'
import { useAuth } from '../contexts/AuthContext'
import MatchCard from './MatchCard'
import logger from '../utils/logger'

export default function PlayPage({ onMatchSelect }) {
  const { user } = useAuth()
  const { data: matches, isLoading, error, refetch } = useMatches()
  const createMatch = useCreateMatch()

  useEffect(() => {
    if (error) logger.capture(error, { source: 'PlayPage', uid: user?.id })
  }, [error])

  const handleCreateAndGo = (matchType) => {
    const today = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    const nameMap = { quick_round: 'Single Round', match: 'Mini Match', trip: 'Golf Trip', team_match: 'Team Match', invitational: 'Invitational' }
    const matchName = `${nameMap[matchType]} - ${today}`
    createMatch.mutate(
      { match_name: matchName, match_type: matchType, status: (matchType === 'invitational' || matchType === 'team_match') ? 'setup' : 'active' },
      {
        onSuccess: (match) => {
          // All match types go through QuickRoundSetup for player add
          onMatchSelect(match.id, { quickRoundSetup: true })
        },
      }
    )
  }

  const activeMatches = (matches ?? []).filter((m) => m.status !== 'completed').sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
  const myMatches = activeMatches.filter((m) => m.owner_id === user?.id)
  const otherMatches = activeMatches.filter((m) => m.owner_id !== user?.id)
  const hasActiveSession = myMatches.length > 0

  return (
    <div>
      {/* Active Match — user's own active matches, shown above everything */}
      {!isLoading && !error && myMatches.length > 0 && (
        <div style={{
          marginBottom: '1.5rem',
          padding: '1rem',
          borderRadius: '0.75rem',
          border: '2px solid var(--accent-primary)',
          backgroundColor: 'rgba(var(--accent-primary-rgb, 14, 164, 122), 0.05)',
        }}>
          <h3 style={{
            fontSize: '0.75rem',
            fontWeight: '600',
            color: 'var(--accent-primary)',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            marginBottom: '0.75rem',
          }}>
            Active Match
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {myMatches.map((match) => (
              <MatchCard key={match.id} match={match} onClick={() => onMatchSelect(match.id)} />
            ))}
          </div>
        </div>
      )}

      <h2 style={{
        fontSize: '1.25rem',
        fontWeight: '700',
        marginBottom: '1rem',
      }}>
        Start Playing
      </h2>

      {/* Creation grid */}
      <div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '0.5rem',
            marginBottom: hasActiveSession ? '0.75rem' : '1.5rem',
          }}>
            {/* Single Round — full-width tall card */}
            <button
              onClick={hasActiveSession ? undefined : () => handleCreateAndGo('quick_round')}
              disabled={hasActiveSession || createMatch.isPending}
              style={{
                gridColumn: 'span 2',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '0.75rem',
                padding: '0.8rem 1rem',
                backgroundColor: 'var(--bg-secondary)',
                borderRadius: '0.75rem',
                border: '1px solid var(--border-color)',
                textAlign: 'center',
                opacity: (hasActiveSession || createMatch.isPending) ? 0.4 : 1,
                cursor: hasActiveSession ? 'default' : undefined,
              }}
            >
              <div style={{
                width: '56px',
                height: '56px',
                borderRadius: '0.75rem',
                backgroundColor: hasActiveSession ? 'var(--bg-tertiary)' : 'rgba(var(--accent-primary-rgb, 14, 164, 122), 0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <FlagTriangleRight size={28} style={{ color: hasActiveSession ? 'var(--text-muted)' : 'var(--accent-primary)' }} />
              </div>
              <div>
                <div style={{ fontWeight: '600', fontSize: '1.0625rem' }}>Single Round</div>
                <div style={{ color: 'var(--text-muted)', fontSize: '0.8125rem', marginTop: '0.125rem' }}>Up to 4 players</div>
              </div>
            </button>

            {/* Row 2: Mini Match + Team Match */}
            <button
              onClick={hasActiveSession ? undefined : () => handleCreateAndGo('match')}
              disabled={hasActiveSession || createMatch.isPending}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '0.75rem',
                padding: '0.8rem 1rem',
                backgroundColor: 'var(--bg-secondary)',
                borderRadius: '0.75rem',
                border: '1px solid var(--border-color)',
                textAlign: 'center',
                opacity: (hasActiveSession || createMatch.isPending) ? 0.4 : 1,
                cursor: hasActiveSession ? 'default' : undefined,
              }}
            >
              <div style={{
                width: '56px',
                height: '56px',
                borderRadius: '0.75rem',
                backgroundColor: hasActiveSession ? 'var(--bg-tertiary)' : 'rgba(var(--accent-primary-rgb, 14, 164, 122), 0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <Trophy size={28} style={{ color: hasActiveSession ? 'var(--text-muted)' : 'var(--accent-primary)' }} />
              </div>
              <div>
                <div style={{ fontWeight: '600', fontSize: '1.0625rem' }}>Mini Match</div>
                <div style={{ color: 'var(--text-muted)', fontSize: '0.8125rem', marginTop: '0.125rem' }}>Single round up to 8 players</div>
              </div>
            </button>

            <button
              onClick={hasActiveSession ? undefined : () => handleCreateAndGo('team_match')}
              disabled={hasActiveSession || createMatch.isPending}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '0.75rem',
                padding: '0.8rem 1rem',
                backgroundColor: 'var(--bg-secondary)',
                borderRadius: '0.75rem',
                border: '1px solid var(--border-color)',
                textAlign: 'center',
                opacity: (hasActiveSession || createMatch.isPending) ? 0.4 : 1,
                cursor: hasActiveSession ? 'default' : undefined,
              }}
            >
              <div style={{
                width: '56px',
                height: '56px',
                borderRadius: '0.75rem',
                backgroundColor: hasActiveSession ? 'var(--bg-tertiary)' : 'rgba(var(--accent-primary-rgb, 14, 164, 122), 0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <Users size={28} style={{ color: hasActiveSession ? 'var(--text-muted)' : 'var(--accent-primary)' }} />
              </div>
              <div>
                <div style={{ fontWeight: '600', fontSize: '1.0625rem' }}>Team Match</div>
                <div style={{ color: 'var(--text-muted)', fontSize: '0.8125rem', marginTop: '0.125rem' }}>Single round team-based event for 8+ players</div>
              </div>
            </button>

            {/* Row 3: Golf Trip + Invitational */}
            <button
              onClick={hasActiveSession ? undefined : () => handleCreateAndGo('trip')}
              disabled={hasActiveSession || createMatch.isPending}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '0.75rem',
                padding: '0.8rem 1rem',
                backgroundColor: 'var(--bg-secondary)',
                borderRadius: '0.75rem',
                border: '1px solid var(--border-color)',
                textAlign: 'center',
                opacity: (hasActiveSession || createMatch.isPending) ? 0.4 : 1,
                cursor: hasActiveSession ? 'default' : undefined,
              }}
            >
              <div style={{
                width: '56px',
                height: '56px',
                borderRadius: '0.75rem',
                backgroundColor: hasActiveSession ? 'var(--bg-tertiary)' : 'rgba(var(--accent-primary-rgb, 14, 164, 122), 0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <MapPin size={28} style={{ color: hasActiveSession ? 'var(--text-muted)' : 'var(--accent-primary)' }} />
              </div>
              <div>
                <div style={{ fontWeight: '600', fontSize: '1.0625rem' }}>Golf Trip</div>
                <div style={{ color: 'var(--text-muted)', fontSize: '0.8125rem', marginTop: '0.125rem' }}>Multi-round events up to 8 players</div>
              </div>
            </button>

            <button
              onClick={hasActiveSession ? undefined : () => handleCreateAndGo('invitational')}
              disabled={hasActiveSession || createMatch.isPending}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '0.75rem',
                padding: '0.8rem 1rem',
                backgroundColor: 'var(--bg-secondary)',
                borderRadius: '0.75rem',
                border: '1px solid var(--border-color)',
                textAlign: 'center',
                opacity: (hasActiveSession || createMatch.isPending) ? 0.4 : 1,
                cursor: hasActiveSession ? 'default' : undefined,
              }}
            >
              <div style={{
                width: '56px',
                height: '56px',
                borderRadius: '0.75rem',
                backgroundColor: hasActiveSession ? 'var(--bg-tertiary)' : 'rgba(var(--accent-primary-rgb, 14, 164, 122), 0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <Plane size={28} style={{ color: hasActiveSession ? 'var(--text-muted)' : 'var(--accent-primary)' }} />
              </div>
              <div>
                <div style={{ fontWeight: '600', fontSize: '1.0625rem' }}>Invitational</div>
                <div style={{ color: 'var(--text-muted)', fontSize: '0.8125rem', marginTop: '0.125rem' }}>Multi-round team-based event for 8+ players</div>
              </div>
            </button>

            {/* League — full-width banner */}
            <button
              disabled
              style={{
                gridColumn: 'span 2',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.75rem',
                padding: '0.8rem 1rem',
                backgroundColor: 'var(--bg-secondary)',
                borderRadius: '0.75rem',
                border: '1px solid var(--border-color)',
                textAlign: 'left',
                opacity: 0.4,
                cursor: 'default',
              }}
            >
              <div style={{
                width: '56px',
                height: '56px',
                borderRadius: '0.75rem',
                backgroundColor: 'var(--bg-tertiary)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}>
                <Shield size={28} style={{ color: 'var(--text-muted)' }} />
              </div>
              <div>
                <div style={{ fontWeight: '600', fontSize: '1.0625rem' }}>League</div>
                <div style={{ color: 'var(--text-muted)', fontSize: '0.8125rem', marginTop: '0.125rem' }}>Coming Soon</div>
              </div>
            </button>
          </div>
          {hasActiveSession && (
            <p style={{
              color: 'var(--text-muted)',
              fontSize: '0.75rem',
              textAlign: 'center',
              marginBottom: '1.5rem',
            }}>
              Finish or complete your active match to start a new one
            </p>
          )}
        </div>

      {/* Active matches */}
      {error ? (
        <div style={{
          padding: '1rem',
          borderRadius: '0.5rem',
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          marginBottom: '1rem',
        }}>
          <p style={{ color: '#ef4444', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.25rem' }}>
            Error loading matches
          </p>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>
            {error.message}
          </p>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.625rem', marginTop: '0.5rem', fontFamily: 'monospace' }}>
            uid: {user?.id?.slice(0, 8)}...
          </p>
          <button
            onClick={() => refetch()}
            style={{
              marginTop: '0.75rem',
              padding: '0.5rem 1rem',
              borderRadius: '9999px',
              fontSize: '0.8125rem',
              fontWeight: '600',
              color: '#fff',
              backgroundColor: 'var(--accent-primary)',
            }}
          >
            Retry
          </button>
        </div>
      ) : isLoading ? (
        <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '2rem' }}>
          Loading...
        </p>
      ) : otherMatches.length > 0 ? (
        <div>
          <h3 style={{
            fontSize: '0.75rem',
            fontWeight: '600',
            color: 'var(--text-muted)',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            marginBottom: '0.75rem',
          }}>
            Other Active Sessions
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {otherMatches.map((match) => (
              <MatchCard key={match.id} match={match} onClick={() => onMatchSelect(match.id)} />
            ))}
          </div>
        </div>
      ) : activeMatches.length === 0 ? (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '20vh',
          gap: '1rem',
          textAlign: 'center',
        }}>
          <div style={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            backgroundColor: 'var(--bg-tertiary)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <span style={{ fontSize: '2rem' }}>⛳</span>
          </div>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
            No active events. Start one above!
          </p>
        </div>
      ) : null}
    </div>
  )
}
